import { getDefaultStore } from 'jotai';

import type { i18n as I18nInstance } from 'i18next';

import { companyAtom } from '@/atoms/company';
import { reviewAnswerAtom } from '@/atoms/reviewAnswer';
import { userInstanceAtom } from '@/atoms/userDetails';
import { resolveManagerLabels } from '@/utils/managerLabel';

/**
 * Feeds `{{managerLabel}}` and `{{managerPluralLabel}}` to every sentence that carries them, so a
 * screen naming the role does not have to thread the word down to each component. A caller that
 * passes the value itself still wins: i18next merges defaults under the caller's own values.
 *
 * This is GLOBAL MUTABLE STATE on the shared i18next instance. It is written from one place only —
 * this module — and a test that starts it must call the returned stop to put the instance back,
 * or the next test reads the previous test's company.
 *
 * It takes the instance as an argument rather than importing it: `@/plugins/i18n` is what starts
 * this, and importing it back would close a cycle the dependency rules reject.
 */
export const startManagerLabelDefaults = (i18n: I18nInstance): (() => void) => {
    const store = getDefaultStore();
    const previousVariables = i18n.options.interpolation?.defaultVariables;

    const apply = () => {
        const company = store.get(companyAtom);
        const reviewAnswer = store.get(reviewAnswerAtom);
        const user = store.get(userInstanceAtom);

        if (!i18n.options.interpolation) {
            i18n.options.interpolation = {};
        }

        i18n.options.interpolation.defaultVariables = resolveManagerLabels({
            language: i18n.language,
            defaultLanguage: company?.company.defaultLanguage,
            identifiers: company?.company.managerIdentifiers ?? reviewAnswer?.managerIdentifiers,
            pluralIdentifiers:
                company?.company.managerPluralIdentifiers ?? reviewAnswer?.managerPluralIdentifiers,
            userLabel: user?.managerLabel,
            userPluralLabel: user?.managerPluralLabel,
            translate: (key) => i18n.t(key),
        });
    };

    apply();

    const unsubscribes = [
        store.sub(companyAtom, apply),
        store.sub(reviewAnswerAtom, apply),
        store.sub(userInstanceAtom, apply),
    ];
    i18n.on('languageChanged', apply);

    return () => {
        unsubscribes.forEach((unsubscribe) => unsubscribe());
        i18n.off('languageChanged', apply);
        if (i18n.options.interpolation) {
            i18n.options.interpolation.defaultVariables = previousVariables;
        }
    };
};
